import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { PauseCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useShopStatus, usePauseShop } from '@/hooks/useShopStatus';

// null = indefinite ("until I resume").
const PRESETS: Array<{ minutes: number | null; labelKey: string }> = [
  { minutes: 15, labelKey: 'shop.pause.presets.15' },
  { minutes: 30, labelKey: 'shop.pause.presets.30' },
  { minutes: 60, labelKey: 'shop.pause.presets.60' },
  { minutes: null, labelKey: 'shop.pause.presets.indefinite' },
];

/**
 * "Stop accepting orders" trigger for the cashier. Hidden while already paused
 * (the PausedBanner owns the resume action then). Opens a dialog to pick a
 * duration (timed auto-resume) or pause indefinitely, with an optional reason
 * shown to customers.
 */
export function ShopPauseControl() {
  const { t } = useTranslation();
  const { data: status } = useShopStatus();
  const pause = usePauseShop();

  const [open, setOpen] = useState(false);
  const [minutes, setMinutes] = useState<number | null>(30);
  const [reason, setReason] = useState('');

  // Only offer "pause" when currently accepting — resume lives on the banner.
  if (!status || status.paused) return null;

  const submit = () => {
    pause.mutate(
      { durationMinutes: minutes, reason: reason.trim() || null },
      {
        onSuccess: () => {
          toast.success(t('shop.pause.toasts.paused'));
          setOpen(false);
          setReason('');
        },
        onError: (err) => toast.error(t('shop.pause.toasts.failed', { message: err.message })),
      },
    );
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <PauseCircle className="mr-2 h-4 w-4" />
        {t('shop.pause.action')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('shop.pause.dialog.title')}</DialogTitle>
            <DialogDescription>{t('shop.pause.dialog.description')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="label-tracked text-[10px]">{t('shop.pause.dialog.duration')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.labelKey}
                    type="button"
                    onClick={() => setMinutes(p.minutes)}
                    className={cn(
                      'rounded-md border px-3 py-2.5 text-sm font-medium transition-colors',
                      minutes === p.minutes
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:bg-accent',
                    )}
                  >
                    {t(p.labelKey)}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="pause-reason" className="label-tracked text-[10px]">
                {t('shop.pause.dialog.reasonLabel')}
              </Label>
              <Textarea
                id="pause-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('shop.pause.dialog.reasonPlaceholder')}
                maxLength={255}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={pause.isPending}>
              {t('shop.pause.dialog.cancel')}
            </Button>
            <Button onClick={submit} disabled={pause.isPending}>
              {t('shop.pause.dialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
